import { QuestionnaireQuestions } from '../models';
import { Questionnaire, QuestionnaireProps } from './Questionnaire';
import { kebabCaseToCamelCase } from '../utils/camelCaseKebabCase';
import { getNextQuestionId as findNextQuestionId } from '../utils/getNextQuestionId';
import { getRoutes } from '../utils/getRoutes';
import { useEffect, useState } from 'react';
import { PageNotFound } from '../components';

export type HistoryInfoProps = {
  replaceState?: boolean;
};

export type QuestionnaireRouterProps = {
  basePath: string;
  routing: {
    path: () => string;
    navigate: (path: string, historyInfo?: HistoryInfoProps) => void;
  };
};

export const QuestionnaireRouter = <
  Questionnaire extends QuestionnaireQuestions,
  GroupId,
  ExtendedComponentTypes
>({
  questionnaireAnswers,
  questionnaire,
  onAnswer,
  configuration,
  featureName,
  basePath,
  questionnaireUrl,
  routing,
  questionId,
}: QuestionnaireProps<Questionnaire, GroupId, ExtendedComponentTypes> &
  QuestionnaireRouterProps) => {
  const routeQuestionId = kebabCaseToCamelCase((questionId as string) ?? '');

  const [currentQuestionId, setCurrentQuestionId] =
    useState<keyof Questionnaire>('');
  const questionnairePrefix = `${basePath}${
    questionnaireUrl ? `/${questionnaireUrl}` : ''
  }`;
  const routes =
    questionnaire.routes ??
    getRoutes<Questionnaire, GroupId, ExtendedComponentTypes>(
      questionnaire.components,
      questionnairePrefix
    );

  const nextId = findNextQuestionId(
    routeQuestionId ?? currentQuestionId,
    questionnaire.components,
    questionnaireAnswers,
    questionnaire.rules,
    true,
    configuration
  );

  const nextPath = routes[nextId]?.path;

  const activePathName = routing.path();
  const navigate = routing.navigate;

  useEffect(() => {
    // bail out if the next path is undefined or the current location
    // is not part of the base path
    if (nextPath === undefined || !activePathName.includes(basePath)) {
      return;
    }
    if (!currentQuestionId || currentQuestionId !== routeQuestionId) {
      setCurrentQuestionId(nextId);
    }
    if (activePathName !== nextPath) {
      navigate(nextPath, { replaceState: true });
    }
  }, [
    currentQuestionId,
    routeQuestionId,
    nextPath,
    nextId,
    basePath,
    activePathName,
    navigate,
  ]);

  if (nextPath === undefined) {
    return <PageNotFound />;
  }

  const onSubmitAnswer = (
    currentState: Partial<Questionnaire>,
    questionId: keyof Questionnaire,
    historyInfo?: HistoryInfoProps
  ) => {
    const question = findNextQuestionId(
      questionId,
      questionnaire.components,
      currentState,
      questionnaire.rules,
      undefined,
      configuration
    );

    if (question) {
      navigate(routes[question].path ?? basePath, historyInfo);
      setCurrentQuestionId(question);
    } else {
      navigate(questionnairePrefix, historyInfo);
    }
  };

  const onProgressClick = (questionId: keyof Questionnaire) => {
    navigate(routes[questionId].path);
  };

  const extendedConfiguration = configuration?.progressBar
    ? {
        ...configuration,
        progressBar: { ...configuration.progressBar, onProgressClick },
      }
    : configuration;
  return (
    <Questionnaire
      featureName={featureName}
      questionnaire={questionnaire}
      questionnaireAnswers={questionnaireAnswers}
      onAnswer={onAnswer}
      questionId={currentQuestionId}
      configuration={extendedConfiguration}
      onSubmitAnswer={onSubmitAnswer}
      navigateTo={routing.navigate}
    />
  );
};
